// frontend/src/content/blog/blogIndex.js
//
// LIGHT per-article metadata: everything needed to LIST, LINK, ORDER or TEASE an
// article - and nothing that only the article page renders.
//
// WHY THIS FILE EXISTS (please don't undo it): App.js imports Homepage EAGERLY,
// so every module the homepage reaches lands in the main bundle that every
// anonymous landing-page visitor downloads. The homepage's blog strip
// (components/homepage/BlogLive.js) needs five fields per article -
// it must import from HERE, never from blogData.js, or ~85KB of article prose
// (blocks/faq/cta/tldr) ships to people who only came to read the landing page.
// See blogBodies.js for the other half.
//
// Adding an article? Metadata goes here, body goes in blogBodies.js, under the
// SAME slug. Every field lives in exactly one of the two files - blogData.js
// composes them back into the `articles` array that remains the public API, so
// consumers are unaffected. scripts/checkBlogSplit.js enforces slug-set parity
// in the frontend build; jest gates merges too, since 2026-09-01 (backlog #60
// closed - it runs inside the required `Frontend (build + ESLint)` check).
//
// IMPORTANT: when you add/remove an article, also update:
//   - frontend/scripts/prerender.js  (ROUTES - add /blog/<slug>)
//   - frontend/public/sitemap.xml    (add the <url> entry)

// From the dependency-free util, NOT from components/Seo - tests that mock the
// Seo component must not take URL construction down with them.
import { buildCanonicalUrl } from "../../utils/canonicalUrl";
// The canonical twelve month names - see formatHebrewDate below.
import { monthName } from "../../utils/monthNames";

export const BLOG_BASE = "/blog";

// ---------------------------------------------------------------------------
// Absolute blog URLs - the ONLY sanctioned way to build one.
//
// WHY THIS EXISTS: these URLs were previously hand-built at six call sites as
// `${SITE_URL}${BLOG_BASE}/${slug}` - i.e. WITHOUT the trailing slash - while
// Seo's canonical tag emitted the WITH-slash form. Every article therefore
// disagreed with its own canonical by one character, and every share link
// resolved through a 301. Delegating to buildCanonicalUrl makes the agreement
// structural (same function, same output) instead of coincidental, and inherits
// its double-slash safety.
//
// NOTE: these are for ABSOLUTE URLs only - canonical/JSON-LD/share/sitemap.
// React Router `to=` props and Seo's `path=` prop take the slash-less ROUTE
// path (`${BLOG_BASE}/${slug}`) and must NOT use these helpers.
// ---------------------------------------------------------------------------

/** Absolute canonical URL of the blog index (trailing slash). */
export const blogIndexUrl = () => buildCanonicalUrl(BLOG_BASE);

/**
 * Absolute canonical URL of one article (trailing slash).
 * @param {string} slug Article slug, with or without surrounding slashes.
 */
export const articleUrl = (slug) => buildCanonicalUrl(`${BLOG_BASE}/${slug}`);

// The visible byline (ISO date -> "30 ביוני 2026"). The month names come from
// the ONE canonical list (utils/monthNames.js) - the genitive prefix `ב` is
// this byline's own grammar, not part of the name, so it stays here at the
// call site and the shared list keeps rendering bare names everywhere else.
// That module is dependency-free, so the eager-homepage bundle note above is
// unaffected.
export function formatHebrewDate(iso) {
  // iso = "YYYY-MM-DD" — parsed manually to avoid timezone drift.
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  return `${d} ב${monthName(m, true)} ${y}`;
}

export const BLOG_AUTHOR = "צוות לידיפיי (Leadify)";

export const articleIndex = [
  // 1 - leads-from-facebook-groups
  {
    slug: "leads-from-facebook-groups",
    image: "/blog-images/leads-from-facebook-groups.png",
    title: "איך משיגים לידים מקבוצות פייסבוק - המדריך המלא",
    metaTitle: "איך משיגים לידים מקבוצות פייסבוק - המדריך המלא לעסקים 2026",
    metaDescription:
      "לידים מקבוצות פייסבוק: איך מזהים הזדמנות אמיתית, תוך כמה זמן להגיב, במה זה שונה ממודעות לידים, ומה סוכני AI מוסיפים לתהליך.",
    datePublished: "2026-06-30",
    dateModified: "2026-09-17",
    tags: ["לידים", "קבוצות פייסבוק", "שיווק לעסקים"],
    excerpt:
      "לידים מקבוצות פייסבוק מגיעים מאנשים שכבר מחפשים שירות. איך לזהות הזדמנות אמיתית, תוך כמה זמן להגיב, במה זה שונה ממודעות לידים, ואיך סוכני AI עושים את זה בשבילכם.",
  },
  // 2 - ai-marketing-small-business
  {
    slug: "ai-marketing-small-business",
    image: "/blog-images/ai-marketing-small-business.png",
    title: "AI לעסקים קטנים: איך משתמשים בבינה מלאכותית לשיווק שמביא לקוחות (מדריך 2026)",
    metaTitle: "AI לעסקים קטנים - שיווק בבינה מלאכותית שמביא לקוחות",
    metaDescription:
      "AI לעסקים קטנים: מה בינה מלאכותית באמת עושה בשיווק, כמה זה עולה בפועל, מתי עדיף סוכנות או כלים, ואיך מתחילים בשלושה צעדים בלי ידע טכני.",
    datePublished: "2026-06-30",
    dateModified: "2026-08-23",
    tags: ["בינה מלאכותית", "AI לעסקים קטנים", "שיווק לעסקים קטנים", "סוכני AI", "לידים"],
    excerpt:
      "בינה מלאכותית לא תשווק במקומכם, אבל היא עושה ארבע עבודות שעסק קטן משלם עליהן ביוקר - והראשונה, איתור מי שמחפש אתכם ממש עכשיו, מניבה את ההחזר הגבוה ביותר. מה כל עבודה שווה, כמה זה עולה, ואיך מתחילים.",
  },
  // 3 - hot-vs-cold-leads
  {
    slug: "hot-vs-cold-leads",
    image: "/blog-images/hot-vs-cold-leads.png",
    title:
      "לידים חמים לעסקים: איך מזהים בקשה אמיתית של לקוח פוטנציאלי",
    metaTitle: "לידים חמים לעסקים - איך מזהים בקשה אמיתית של לקוחות",
    metaDescription:
      "לידים חמים לעסקים: מה זה ליד חם ואיך מזהים בקשה אמיתית של לקוח פוטנציאלי, כמה מהר עונים כדי לא לפספס הזדמנויות עסקיות, ומה לבדוק לפני קנייה.",
    datePublished: "2026-06-30",
    dateModified: "2026-10-19",
    tags: [
      "לידים חמים",
      "לידים חמים לעסקים",
      "ליד פושר",
      "לקוחות פוטנציאליים",
      "הזדמנויות עסקיות",
    ],
    excerpt:
      "מה זה ליד חם ואיך מזהים בקשה אמיתית בתוך פוסט או הודעה: חמשת הסימנים, דוגמאות של רעש מול בקשה, מה המחקר אומר על מהירות התגובה, ומה לדעת לפני שקונים לידים חמים.",
  },
  // 4 - marketing-tasks-to-stop-doing-manually
  {
    slug: "marketing-tasks-to-stop-doing-manually",
    image: "/blog-images/marketing-tasks-to-stop-doing-manually.png",
    title: "5 משימות שיווק שכל בעל עסק צריך להפסיק לעשות ידנית",
    metaTitle: "5 משימות שיווק שכדאי להפסיק לעשות ידנית 2026",
    metaDescription:
      "חמש משימות שיווק שגוזלות לבעלי עסקים שעות יקרות - ואיך סוכני AI מבצעים אותן מהר וטוב יותר, כדי שתתפנו למה שחשוב באמת.",
    datePublished: "2026-06-30",
    dateModified: "2026-06-30",
    tags: ["סוכני AI", "פרודוקטיביות", "שיווק לעסקים"],
    excerpt:
      "חמש משימות שיווק חוזרות שגוזלות שעות - ומה לעשות במקום: להעביר אותן לסוכני AI ולהתפנות לצמיחה.",
  },
  // 5 - first-outreach-message
  {
    slug: "first-outreach-message",
    image: "/blog-images/first-outreach-message.png",
    title: "איך לכתוב הודעת פנייה ראשונה שמקבלת תשובה (עם דוגמאות)",
    metaTitle: "איך לכתוב הודעת פנייה ראשונה שמקבלת תשובה - עם דוגמאות",
    metaDescription:
      "נוסחה פשוטה להודעת פנייה ראשונה שמקבלת תשובה: מבנה, טון, אורך וטעויות להימנע מהן - כולל דוגמאות מוכנות לשימוש בקבוצות פייסבוק.",
    datePublished: "2026-06-30",
    dateModified: "2026-09-16",
    tags: ["הודעת פנייה", "המרת לידים", "תקשורת עם לקוחות"],
    excerpt:
      "הודעת פנייה ראשונה טובה קצרה, אישית, ומדברת על הצורך של הלקוח. המבנה המנצח, דוגמאות לפני/אחרי, וטעויות שמורידות תגובות.",
  },
  // 6 - lead-follow-up
  {
    slug: "lead-follow-up",
    image: "/blog-images/lead-follow-up.png",
    title: "איך מעקב (פולו-אפ) נכון הופך הזדמנויות ללקוחות",
    metaTitle: "איך מעקב (פולו-אפ) נכון הופך הזדמנויות ללקוחות",
    metaDescription:
      "הודעת מעקב (פולו-אפ) היא מהלך עסקי חכם שמחזיר לחיים הזדמנויות שנפלו בין הכיסאות. מתי לפנות, איך וכמה פעמים, ואיך תזכורות חכמות דואגות שלא תפספסו.",
    datePublished: "2026-06-30",
    dateModified: "2026-06-30",
    tags: ["מעקב", "פולו-אפ", "ניהול לידים"],
    excerpt:
      "מעקב הוא מהלך עסקי חכם והרגל חשוב ליישום. מתי, איך וכמה פעמים לעקוב - ואיך תזכורות חכמות דואגות שאף הזדמנות לא תיפול בין הכיסאות.",
  },
  // 7 - organic-vs-paid-facebook-leads
  {
    slug: "organic-vs-paid-facebook-leads",
    image: "/blog-images/organic-vs-paid-facebook-leads.png",
    title: "לידים אורגניים מול קמפיין ממומן בפייסבוק: מה באמת עדיף לעסק קטן?",
    metaTitle: "לידים אורגניים מול קמפיין ממומן בפייסבוק - מה עדיף לעסק?",
    metaDescription:
      "השוואה בין לידים אורגניים לקמפיין ממומן בפייסבוק: עלות, מהירות, אמון וטווח ארוך - ואיך לשלב נכון כדי להשיג לקוחות בלי לשרוף תקציב. מדריך לעסק קטן.",
    datePublished: "2026-07-01",
    dateModified: "2026-07-01",
    tags: ["לידים אורגניים", "קמפיין ממומן", "עלות ליד", "שיווק לעסק קטן"],
    excerpt:
      "שתי הגישות עובדות - אבל לא באותו אופן. עלות, מהירות, אמון וטווח ארוך, ואיך לשלב נכון כדי להשיג לקוחות בלי לשרוף תקציב.",
  },
  // 8 - build-authority-facebook-groups
  {
    slug: "build-authority-facebook-groups",
    image: "/blog-images/build-authority-facebook-groups.png",
    title: "איך לבנות מוניטין בקבוצות פייסבוק ולהפוך לאוטוריטה בתחום שלכם",
    metaTitle: "איך לבנות מוניטין בקבוצות פייסבוק ולהפוך לאוטוריטה בתחום",
    metaDescription:
      "מדריך לבניית מוניטין וסמכות מקצועית בקבוצות פייסבוק: איך לתת ערך, להגיב נכון ולהפוך לאוטוריטה שאליה פונים - בלי למכור ובלי להציק. צעד אחר צעד.",
    datePublished: "2026-07-01",
    dateModified: "2026-08-06",
    tags: ["בניית מוניטין", "סמכות מקצועית", "קבוצות פייסבוק", "נוכחות אורגנית"],
    excerpt:
      "מוניטין נבנה ממתן ערך עקבי, לא ממכירה. איך לתת ערך, להגיב נכון ולהפוך לאוטוריטה שאליה פונים - בלי למכור ובלי להציק.",
  },
  // 9 - business-website-small-business
  {
    slug: "business-website-small-business",
    image: "/blog-images/business-website-small-business.png",
    title: "אתר תדמית לעסק קטן: למה אתם חייבים עמוד אחד - ואיך מקימים אותו בדקות",
    metaTitle: "אתר תדמית לעסק קטן - למה חשוב ואיך מקימים בדקות 2026",
    metaDescription:
      "למה כל עסק קטן צריך עמוד תדמית אחד, מה חייב להופיע בו כדי שימיר לקוחות, ואיך מקימים אותו בדקות - בלי לשלם אלפי שקלים ובלי לחכות חודשים.",
    datePublished: "2026-07-05",
    dateModified: "2026-07-07",
    tags: ["אתר תדמית", "עמוד עסקי", "נוכחות דיגיטלית", "שיווק לעסק קטן"],
    excerpt:
      "עמוד תדמית אחד, פשוט ומהיר, הוא גשר האמון שהופך פנייה ללקוח. מה חייב להופיע בו, ואיך מקימים אותו בדקות - בלי מעצב ובלי אלפי שקלים.",
  },
  // 10 - wordpress-wix-small-business
  {
    slug: "wordpress-wix-small-business",
    image: "/blog-images/wordpress-wix-small-business.png",
    title: "וורדפרס או וויקס לעסק קטן? המדריך לבחירה נכונה",
    metaTitle: "וורדפרס או וויקס לעסק קטן - מה לבחור ב-2026?",
    metaDescription:
      "מדריך החלטה בין וורדפרס (WordPress) לוויקס (Wix) לעסק קטן: במה כל אחד מצטיין, העלויות הנסתרות, ומתי עדיף עמוד עסק מוכן שמתחבר לאיתור לקוחות - בדקות.",
    datePublished: "2026-07-08",
    dateModified: "2026-07-08",
    tags: ["אתר תדמית", "בניית אתר", "וורדפרס", "וויקס", "שיווק לעסק קטן"],
    excerpt:
      "וורדפרס נותן שליטה וגמישות אינסופית; וויקס נותן פשטות של גרירה ושחרור. אבל לעסק של אדם אחד, שתי הדרכים הן פרויקט. מתי כל אחת מנצחת - ומהי הדרך השלישית.",
  },
  // 11 - vibe-coding-business-website
  {
    slug: "vibe-coding-business-website",
    image: "/blog-images/vibe-coding-business-website.png",
    title: "Vibe Coding: לבנות אתר עם AI - ומתי זה מתאים לעסק שלכם",
    metaTitle: "Vibe Coding - בניית אתר עם AI לעסק קטן 2026",
    metaDescription:
      "מה זה vibe coding, מה Base44 ו-Lovable עושים מצוין, והשאלות שבעל עסק חייב לשאול לפני שבונה אתר עם בינה מלאכותית - מול עמוד עסק מוכן שלוכד פניות בדקות.",
    datePublished: "2026-07-08",
    dateModified: "2026-07-08",
    tags: ["אתר תדמית", "בניית אתר", "בינה מלאכותית", "vibe coding"],
    excerpt:
      "vibe coding מאפשר לבנות אתר בעזרת AI על ידי תיאור בלבד. מה Base44 ו-Lovable עושים מצוין, השאלות שחייבים לשאול לפני שבונים כך אתר עסקי, ומתי עמוד מוכן עדיף.",
  },
  // 12 - facebook-group-scanning-service-vs-tools
  {
    slug: "facebook-group-scanning-service-vs-tools",
    image: "/blog-images/facebook-group-scanning-service-vs-tools.png",
    title: "סריקת קבוצות פייסבוק לאיתור לידים: שירות סוכני AI מול כלים שמפעילים לבד",
    metaTitle: "סריקת קבוצות פייסבוק: שירות סוכני AI מול כלים שמפעילים לבד",
    metaDescription:
      "רוצים לאתר לקוחות פוטנציאליים מקבוצות פייסבוק? השוואה בין חיפוש ידני, כלי סריקה עצמאיים ושירות מנוהל מבוסס סוכני AI - ומתי כל גישה מתאימה.",
    datePublished: "2026-07-19",
    dateModified: "2026-07-27",
    tags: ["סריקת קבוצות פייסבוק", "לידים", "סוכני AI", "קבוצות פייסבוק"],
    excerpt:
      "ארבע דרכים לאתר לקוחות פוטנציאליים מקבוצות פייסבוק - חיפוש ידני, כלי שמפעילים לבד, פרסום המוני ושירות מבוסס סוכני AI. היתרונות, החסרונות ומתי כל גישה מתאימה לעסק שלכם.",
  },
  // 13 - facebook-group-monitoring
  {
    slug: "facebook-group-monitoring",
    image: "/blog-images/facebook-group-monitoring.png",
    title: "ניטור קבוצות פייסבוק לעסקים: איך להפוך שיחות בקבוצות ללקוחות",
    metaTitle: "ניטור קבוצות פייסבוק לעסקים - מדריך לאיתור לידים 2026",
    metaDescription:
      "ניטור קבוצות פייסבוק מזהה לקוחות פוטנציאליים ברגע שהם מחפשים. כמה קבוצות לנטר, איך מפרידים אות מרעש, ומה עושים בדקות שאחרי ההתראות.",
    datePublished: "2026-07-19",
    dateModified: "2026-09-17",
    tags: ["ניטור קבוצות פייסבוק", "לידים", "סוכני AI", "קבוצות פייסבוק", "לקוחות פוטנציאליים"],
    excerpt:
      "ניטור קבוצות פייסבוק הוא האזנה רציפה לשיח - לא חיפוש חד-פעמי. כמה קבוצות באמת צריך, איך מפרידים אות מרעש, ומה עושים בשלושים הדקות הראשונות אחרי התראה.",
  },
  // 14 - choosing-marketing-company
  {
    slug: "choosing-marketing-company",
    image: "/blog-images/choosing-marketing-company.png",
    title: "איך לבחור חברת שיווק שבאמת מביאה לקוחות - מדריך לבעל עסק שכבר נכווה",
    metaTitle: "איך לבחור חברת שיווק שמביאה לידים - מדריך",
    metaDescription:
      "מדריך כן לבחירת חברת שיווק: מה לשאול לפני חתימה, איך לקרוא תיק עבודות, מה כל מודל תמחור אומר על הסיכון שלכם ומהם סימני האזהרה.",
    datePublished: "2026-07-20",
    dateModified: "2026-07-20",
    tags: ["חברת שיווק", "לידים", "בחירת ספק", "שיווק לעסק קטן"],
    excerpt:
      "בחירת חברת שיווק נקבעת פחות לפי המצגת ויותר לפי מה שמוגדר כתוצאה, מי נושא בסיכון ואיזו גישה יש לכם לנתונים בזמן אמת. מדריך מנקודת מבט של הקונה, שיעזור להבדיל בין ספק טוב לספק שמוכר חשיפה ומכנה אותה לידים.",
  },
  // 15 - first-customers-no-sales
  {
    slug: "first-customers-no-sales",
    image: "/blog-images/first-customers-no-sales.png",
    title: "בניתי מוצר ואף אחד לא קונה: איך משיגים את הלקוחות הראשונים",
    metaTitle: "אין לקוחות למוצר חדש, מה עושים? כך משיגים את הראשונים",
    metaDescription:
      "בניתם מוצר או שירות ואף אחד לא קונה? מדריך שמאבחן אם הבעיה היא הפצה, מיצוב, אמון או ביקוש, ומראה איך משיגים את הלקוחות הראשונים בלי תקציב.",
    datePublished: "2026-07-20",
    dateModified: "2026-07-20",
    tags: ["השגת לקוחות", "לקוח ראשון", "שיווק לעסק חדש", "הפצה"],
    excerpt:
      "כשאף אחד לא קונה, כמעט תמיד מדובר באחת מארבע בעיות: הפצה, מיצוב, אמון או ביקוש. כל אחת דורשת פעולה אחרת, ולכן הצעד הראשון הוא לאבחן ולא לפעול. כך משיגים את הלקוחות הראשונים בלי תקציב.",
  },
  // 16 - business-website-cost
  {
    slug: "business-website-cost",
    image: "/blog-images/business-website-cost.png",
    title: "כמה עולה אתר לעסק קטן ב-2026 - פירוק מחירים אמיתי",
    metaTitle: "כמה עולה אתר לעסק קטן ב-2026 - מחירים אמיתיים",
    metaDescription:
      "פירוק מחירים כנה לאתר לעסק קטן ב-2026: פרילנסר, סטודיו, וויקס, וורדפרס, בנייה עם AI ועמוד מוכן - כולל כל העלויות הנסתרות שאף אחד לא מפרסם.",
    datePublished: "2026-07-20",
    dateModified: "2026-09-17",
    tags: ["אתר תדמית", "עלות בניית אתר", "בניית אתר", "שיווק לעסק קטן"],
    excerpt:
      "כמה באמת עולה אתר לעסק קטן בישראל ב-2026? פירוק כן של חמישה מסלולי בנייה - מפרילנסר ועד עמוד מוכן - כולל העלויות הנסתרות של אחסון, דומיין, תחזוקה ושינויים אחרי המסירה שקובעות את המחיר האמיתי.",
  },
  // 17 - can-ai-bring-customers
  {
    slug: "can-ai-bring-customers",
    image: "/blog-images/can-ai-bring-customers.png",
    title: "האם AI באמת יכול להביא לקוחות לעסק - או שזה עוד כלי שיבזבז לי זמן?",
    metaTitle: "האם AI באמת מביא לקוחות לעסק? תשובה כנה",
    metaDescription:
      "תשובה כנה לבעלי עסקים ספקנים: איפה AI באמת מייצר לקוחות פוטנציאליים, איפה הוא לא עוזר בכלל, ואיך לא להפוך כלי AI לעבודה נוספת שלא משלמים עליה.",
    datePublished: "2026-07-20",
    dateModified: "2026-07-20",
    tags: ["בינה מלאכותית", "סוכני AI", "שיווק עם AI", "לידים"],
    excerpt:
      "כן, AI באמת יכול להביא לקוחות - אבל רק כשהוא עושה את העבודה המייגעת של מציאה, סינון ותיעדוף של הזדמנויות עסקיות. הוא לא סוגר עסקאות ולא בונה אמון. ההבדל בין תוצאה לבזבוז זמן הוא מי עושה את העבודה.",
  },
  // 18 - summer-slowdown-business-plan
  {
    slug: "summer-slowdown-business-plan",
    image: "/blog-images/summer-slowdown-business-plan.png",
    title: "עונת המלפפונים: תוכנית 30 יום לעסק שהפניות בו נעצרו",
    metaTitle: "עונת המלפפונים בעסק - תוכנית 30 יום לאוגוסט",
    metaDescription:
      "הפניות נעצרו באוגוסט? מדריך מעשי לעונת המלפפונים: מה באמת קורה לביקוש בקיץ, תוכנית עבודה שבועית ל-30 יום, ואיפה מוצאים לקוחות פוטנציאליים דווקא עכשיו.",
    datePublished: "2026-07-26",
    dateModified: "2026-08-11",
    tags: ["עונת המלפפונים", "שיווק בקיץ", "לידים", "שיווק לעסק קטן"],
    excerpt:
      "חלק מהירידה בפניות בקיץ אמיתי, וחלק ניכר ממנה הוא נבואה שמגשימה את עצמה. איך מפרידים בין השניים, איפה כן יש ביקוש באוגוסט, ותוכנית עבודה שבועית שבונה את הצינור של ספטמבר.",
  },
  // 19 - facebook-group-post-that-converts
  {
    slug: "facebook-group-post-that-converts",
    image: "/blog-images/facebook-group-post-that-converts.png",
    title: "איך לכתוב פוסט בקבוצת פייסבוק שמביא פניות (עם דוגמאות)",
    metaTitle: "איך לכתוב פוסט בקבוצת פייסבוק שמביא פניות",
    metaDescription:
      "מדריך מעשי לכתיבת פוסט בקבוצת פייסבוק שמביא לקוחות פוטנציאליים: המבנה שעובד, שורת הפתיחה, מה לשים בתגובות, והטעויות שגורמות לפוסט להיעלם או להימחק.",
    datePublished: "2026-07-26",
    dateModified: "2026-07-27",
    tags: ["קבוצות פייסבוק", "כתיבת פוסט", "לידים אורגניים", "שיווק לעסק קטן"],
    excerpt:
      "פוסט שמביא פניות נראה שונה לגמרי מפוסט שמפרסם עסק. המבנה בחמישה חלקים, דוגמאות לפני ואחרי, איפה שמים את הקישור, ושש טעויות שגורמות לפוסט להיעלם.",
  },
  // 20 - facebook-group-recommendations
  {
    slug: "facebook-group-recommendations",
    image: "/blog-images/facebook-group-recommendations.png",
    title: "המלצות בקבוצות פייסבוק: איך הופכים לעסק שכולם מתייגים",
    metaTitle: "המלצות בקבוצות פייסבוק: איך הופכים לעסק שכולם מתייגים",
    metaDescription:
      "איך זוכים בהמלצות בקבוצות פייסבוק? איך כלכלת ההמלצות עובדת, למה שאלה בלי כוונת קנייה היא הזדמנות עסקית, ואיך יודעים בזמן אמת שמחפשים אתכם.",
    datePublished: "2026-08-06",
    dateModified: "2026-09-17",
    tags: ["המלצות בפייסבוק", "מוניטין", "קבוצות פייסבוק", "לקוחות פוטנציאליים"],
    excerpt:
      "בקבוצות פייסבוק פועלת כלכלת המלצות: מישהו שואל, חברי הקבוצה מתייגים, והעסק שמתויג שוב ושוב מקבל את הפנייה. איך המנגנון עובד מבפנים, למה שאלה בלי כוונת קנייה היא הזדמנות מוניטין, ואיך לא לפספס את הרגע שבו מחפשים אתכם.",
  },
  // 21 - warming-cold-leads
  {
    slug: "warming-cold-leads",
    image: "/blog-images/warming-cold-leads.png",
    title:
      "חימום לידים והחייאת לידים קרים: איך הופכים ליד קר ללקוח משלם",
    metaTitle: "חימום לידים והחייאת לידים קרים - כך מחזירים פניות לחיים",
    metaDescription:
      "חימום לידים והחייאת לידים קרים צעד אחר צעד: מתי חוזרים לפנייה שנרדמה, מה כותבים, באיזה קצב ומתי עוצרים - עם דוגמאות להודעות שמחזירות שיחה.",
    datePublished: "2026-08-06",
    dateModified: "2026-10-03",
    tags: [
      "חימום לידים",
      "החייאת לידים קרים",
      "לידים קרים",
      "ניהול לידים",
      "תהליך מכירה",
    ],
    excerpt:
      "רוב הלידים שלא נסגרו לא נעלמו - הם התקררו. איך מחממים ליד קר או פושר בשלוש נגיעות, איך מחיים פנייה שנרדמה לפני חודשים, מה כותבים בהודעה ומתי עוצרים - עם דוגמאות וקצב מומלץ.",
  },
  // 22 - word-of-mouth-supporter-network
  {
    slug: "word-of-mouth-supporter-network",
    image: "/blog-images/word-of-mouth-supporter-network.png",
    title: "שיווק מפה לאוזן בקבוצות פייסבוק: איך בונים רשת תומכים שממליצה עליכם ברגע הנכון",
    metaTitle: "שיווק מפה לאוזן בקבוצות פייסבוק - כך בונים רשת תומכים",
    metaDescription:
      "שיווק מפה לאוזן לא חייב להיות עניין של מזל: כך בונים רשת תומכים שממליצה עליכם בתגובות בדיוק כשלקוח פוטנציאלי מחפש - בהסכמה, בלי להציק ובלי ספאם.",
    datePublished: "2026-08-11",
    dateModified: "2026-09-17",
    tags: ["שיווק מפה לאוזן", "המלצות", "רשת תומכים", "קבוצות פייסבוק"],
    excerpt:
      "שיווק מפה לאוזן מביא את הלקוחות הטובים ביותר - אבל אצל רוב העסקים הוא קורה במקרה. איך בונים רשת תומכים בהסכמה, מפעילים אותה ברגע הנכון, ונשארים בצד הנכון של הגבול בין המלצה אמיתית לספאם.",
  },
  // 23 - consistent-customer-flow
  {
    slug: "consistent-customer-flow",
    image: "/blog-images/consistent-customer-flow.png",
    title: "חודש מלא, חודש ריק: למה זרם הלקוחות לא יציב - ואיך מייצבים אותו",
    metaTitle: "חודש מלא, חודש ריק - כך מייצבים את זרם הלקוחות לעסק",
    metaDescription:
      "הפניות לעסק מגיעות בגלים - חודש עמוס ואז שקט? זה דפוס, לא מזל רע. למה זה קורה, ואיך בונים מערכת שיווק מינימלית שמביאה לקוחות בעקביות גם כשאתם עסוקים.",
    datePublished: "2026-08-11",
    dateModified: "2026-08-11",
    tags: ["זרם לקוחות", "שיווק בעקביות", "לידים", "שיווק לעסק קטן"],
    excerpt:
      "הגלים בעסק - חודש שהטלפון לא מפסיק לצלצל ואז שקט - הם דפוס, לא מזל. למה הפניות של היום נקבעות לפני חודשיים, מה אומר כלל ה-95:5, ואיך בונים מערכת שיווק מינימלית שלא קורסת כשאתם עמוסים.",
  },
  // 24 - posting-daily-no-enquiries
  {
    slug: "posting-daily-no-enquiries",
    image: "/blog-images/posting-daily-no-enquiries.png",
    title: "מפרסמים כל יום ואף אחד לא פונה? למה דחיפת תוכן לא מביאה לקוחות - ומה כן מביא",
    metaTitle: "מפרסמים כל יום ואין פניות? למה פרסום לא מביא לקוחות",
    metaDescription:
      "פרסום יומי, קמפיין ופניות קרות ובכל זאת אף אחד לא פונה? הבעיה היא כיוון, לא כמות. איך מאתרים את מי שמחפש אתכם ממש עכשיו - ואיך פונים אליו נכון.",
    datePublished: "2026-08-23",
    dateModified: "2026-08-23",
    tags: ["לידים", "שיווק לעסק קטן", "סוכני AI", "קבוצות פייסבוק"],
    excerpt:
      "מפרסמים כל יום, מריצים קמפיין, שולחים פניות קרות - ועדיין אף פנייה? הבעיה היא כיוון, לא כמות: פרסום דוחף מסר למי שלא מחפש, בזמן שאנשים באזורכם כותבים בקבוצות שהם מחפשים בדיוק אתכם. איך מאתרים אותם ועונים נכון.",
  },
  // 25 - paid-ads-reality-check
  {
    slug: "paid-ads-reality-check",
    image: "/blog-images/paid-ads-reality-check.png",
    title: "פרסום ממומן לעסק קטן: מה באמת סביר לקבל - במספרים",
    metaTitle: "פרסום ממומן לעסק קטן: כמה לידים באמת סביר לקבל ביום",
    metaDescription:
      "פרסום ממומן לעסק קטן: כמה באמת עולה ליד, מה סביר לקבל מתקציב יומי קטן, איך מחשבים עלות ללקוח ולא רק לליד, ומתי עדיף לעבור לאיתור אורגני.",
    datePublished: "2026-08-31",
    dateModified: "2026-09-17",
    tags: ["פרסום ממומן", "לידים", "תקציב שיווק", "עסקים קטנים"],
    excerpt:
      "משלמים ריטיינר חודשי ומוציאים תקציב יומי, ולא יודעים אם התוצאות סבירות. הנה המספרים המאומתים שאפשר להשוות אליהם, והחישוב שמראה כמה באמת עולה לכם לקוח.",
  },
  // 26 - where-to-get-leads-israel
  {
    slug: "where-to-get-leads-israel",
    image: "/blog-images/where-to-get-leads-israel.png",
    title: "מאיפה משיגים לידים לעסק בישראל - כל המקורות בהשוואה אחת (2026)",
    metaTitle: "מאיפה משיגים לידים לעסק בישראל - כל המקורות בהשוואה - 2026",
    metaDescription:
      "שמונה מקורות לידים ולקוחות פוטנציאליים לעסק בישראל: קבוצות פייסבוק, פרסום ממומן, גוגל, קניית לידים והפניות - עלות, זמן עד פנייה ראשונה ובלעדיות.",
    datePublished: "2026-09-06",
    dateModified: "2026-09-16",
    tags: ["לידים", "שיווק לעסק קטן", "קבוצות פייסבוק", "פרסום ממומן"],
    excerpt:
      "שמונה דרכים להשיג לקוחות בישראל, מקבוצות פייסבוק ועד קניית לידים, מושוות על אותו סרגל: עלות, זמן עד הפנייה הראשונה, מאמץ שבועי ובלעדיות - ולמי כל אחת מהן לא מתאימה.",
  },
  // 27 - leads-for-lawyers
  {
    slug: "leads-for-lawyers",
    image: "/blog-images/leads-for-lawyers.png",
    title:
      "לידים לעורכי דין מקבוצות פייסבוק: מה מותר, מה עובד ומה מפספסים - 2026",
    metaTitle: "לידים לעורכי דין מקבוצות פייסבוק: מה מותר ומה עובד - 2026",
    metaDescription:
      "לידים לעורכי דין מקבוצות פייסבוק: מה מתירים כללי הפרסומת של הלשכה, האם תגובה לפוסט היא שידול, איך עונים נכון ותוך כמה זמן.",
    datePublished: "2026-09-16",
    dateModified: "2026-09-22",
    tags: ["עורכי דין", "לידים", "קבוצות פייסבוק", "כללי פרסומת"],
    excerpt:
      "עורכי דין רואים בקבוצות פייסבוק פוסטים של אנשים שמחפשים ייצוג, ולא בטוחים אם מותר להם לענות. מה מתירים כללי הפרסומת של הלשכה, האם תגובה לפוסט פומבי היא שידול ואיך נראית תגובה שמביאה פנייה בלי לחצות את הגבול.",
  },
  // 28 - leads-for-accountants
  {
    slug: "leads-for-accountants",
    image: "/blog-images/leads-for-accountants.png",
    title:
      "לידים לרואי חשבון ומנהלי חשבונות: איך משיגים לקוחות בלי להפר את התקנות",
    metaTitle: "לידים לרואי חשבון ומנהלי חשבונות - מה מותר לפי התקנות",
    metaDescription:
      "לידים לרואי חשבון ומנהלי חשבונות: מה מותר לפי תקנות רואי חשבון, איך מזהים בקבוצות פייסבוק מי מחפש רואה חשבון עכשיו ואיך עונים לו נכון.",
    datePublished: "2026-09-16",
    dateModified: "2026-09-24",
    tags: ["רואי חשבון", "מנהלי חשבונות", "לידים", "קבוצות פייסבוק", "לקוחות פוטנציאליים"],
    excerpt:
      "רואה חשבון שנתקל בקבוצה בפוסט \"מחפשת רואה חשבון, ממליצים?\" צריך לדעת שני דברים: מה התקנות מתירות לו לכתוב ואיך עונים כך שהפנייה תגיע אליו. המדריך עובר על תקנה 2, על הדרך המעשית לענות ועל מה שונה אצל מנהלות חשבונות ויועצי מס.",
  },
  // 29 - leads-for-insurance-financial-advisors
  {
    slug: "leads-for-insurance-financial-advisors",
    image: "/blog-images/leads-for-insurance-financial-advisors.png",
    title:
      "לידים לסוכני ביטוח ויועצים פיננסיים: איפה הלקוחות שואלים, ומה חובה לגלות",
    metaTitle: "לידים לסוכני ביטוח ויועצים פיננסיים: מה מותר לענות בקבוצה",
    metaDescription:
      "לידים לסוכני ביטוח ויועצים פיננסיים מקבוצות פייסבוק: איפה שואלים על פנסיה, משכנתא והשקעות, מה חובת הגילוי הנאות דורשת ואיך עונים נכון.",
    datePublished: "2026-09-16",
    dateModified: "2026-09-24",
    tags: ["סוכני ביטוח", "ייעוץ פיננסי", "לידים", "קבוצות פייסבוק", "פנסיה"],
    excerpt:
      "בקבוצות פייסבוק שואלים בפומבי על ביטוח רכב, משכנתא, פנסיה והשקעות, וכל שאלה כזאת היא הזדמנות גלויה. מה חובת הגילוי הנאות מחייבת אתכם לכתוב בתגובה הראשונה, איפה עובר הקו הפלילי בשאלות השקעה ואיך עונים כך שיפנו דווקא אליכם.",
  },
  // 30 - leads-for-dog-trainers
  {
    slug: "leads-for-dog-trainers",
    image: "/blog-images/leads-for-dog-trainers.png",
    title:
      "לידים למאלפי כלבים: איך קבוצות הפייסבוק המקומיות מביאות פניות חדשות",
    metaTitle: "לידים למאלפי כלבים - איך מקבלים פניות מקבוצות פייסבוק",
    metaDescription:
      "לידים למאלפי כלבים מקבוצות פייסבוק: איפה בעלי הכלבים מבקשים המלצה, איך עונים כדי לקבל המלצות בשרשור ואילו כלי AI עוזרים להביא לקוחות.",
    datePublished: "2026-09-17",
    dateModified: "2026-09-24",
    tags: [
      "מאלפי כלבים",
      "אילוף כלבים",
      "לידים",
      "קבוצות פייסבוק",
      "לקוחות פוטנציאליים",
    ],
    excerpt:
      "בעלי כלבים מחפשים מאלף בקבוצת הפייסבוק של היישוב, בפוסט שמבקש המלצה ונסגר תוך שעות. המדריך מסביר איפה נמצאות הפניות האלה, איך עונים כך שמקבלים המלצות מהשרשור עצמו, ואילו כלי AI באמת עוזרים למאלף כלבים להביא לקוחות.",
  },
  // 31 - leads-for-web-developers
  {
    slug: "leads-for-web-developers",
    image: "/blog-images/leads-for-web-developers.png",
    title:
      "לידים לבוני אתרים ומפתחי אפליקציות: איך מזהים פרויקט אמיתי בקבוצות פייסבוק",
    metaTitle: "לידים לבוני אתרים ומפתחים: איך מזהים פרויקט אמיתי - 2026",
    metaDescription:
      "לידים לבוני אתרים ומפתחי אפליקציות מקבוצות פייסבוק: איך מזהים פרויקט אמיתי מול סקר מחירים, איך עונים נכון ואילו כלי AI עוזרים להביא לקוחות.",
    datePublished: "2026-09-17",
    dateModified: "2026-09-24",
    tags: [
      "בוני אתרים",
      "מפתחי אפליקציות",
      "לידים",
      "קבוצות פייסבוק",
      "פרילנסרים",
    ],
    excerpt:
      "בונה אתרים שרואה בקבוצה \"מחפש מישהו שיבנה לי אתר\" צריך להחליט מהר אם זה פרויקט או משהו שהשואל יבנה לבד בכלי מוכן. איך מפרידים בין השניים, איך נראית תגובה שמביאה פנייה, ואיך תשובה אחת בשרשור מייצרת המלצות.",
  },
  // 32 - leads-for-digital-marketers
  {
    slug: "leads-for-digital-marketers",
    image: "/blog-images/leads-for-digital-marketers.png",
    title:
      "לידים לאנשי שיווק דיגיטלי ומנהלי קמפיינים: איפה בעלי עסקים מבקשים עזרה, ואיך עונים בלי למכור",
    metaTitle: "לידים לאנשי שיווק דיגיטלי ומנהלי קמפיינים - מה עובד",
    metaDescription:
      "לידים לאנשי שיווק דיגיטלי ומנהלי קמפיינים: איפה בעלי עסקים מבקשים עזרה בקבוצות פייסבוק, איך משיגים לקוחות לסוכנות קטנה ואיך עונים בלי למכור.",
    datePublished: "2026-09-17",
    dateModified: "2026-09-24",
    tags: ["שיווק דיגיטלי", "מנהלי קמפיינים", "לידים", "קבוצות פייסבוק", "סוכנויות שיווק"],
    excerpt:
      "אנשי שיווק דיגיטלי ומנהלי קמפיינים רואים בקבוצות פייסבוק בעלי עסקים שמבקשים עזרה, ונכנסים לשרשור שכבר מלא בקולגות. איך מזהים בקשה אמיתית, איך נראית תגובה שמביאה לקוח בלי למכור ואיך תשובה אחת הופכת להמלצות ולעבודה חוזרת.",
  },
  // 33 - answering-recommendation-requests
  {
    slug: "answering-recommendation-requests",
    image: "/blog-images/answering-recommendation-requests.png",
    title:
      "מי מכיר בעל מקצוע? להגיב בפוסט או בהודעה פרטית - המדריך לבעלי עסקים",
    metaTitle: "פוסט \"מי מכיר\" בקבוצה: להגיב בפוסט או לשלוח הודעה פרטית?",
    metaDescription:
      "להגיב בפוסט או לשלוח הודעה פרטית? מדריך למענה לבקשת המלצה בקבוצת פייסבוק: מה כותבים בתגובה, מה בהודעה הפרטית ואיך לא להישמע כמו פרסומת.",
    datePublished: "2026-09-17",
    dateModified: "2026-10-03",
    tags: [
      "קבוצות פייסבוק",
      "לידים",
      "לקוחות פוטנציאליים",
      "בעלי עסקים",
      "הזדמנויות עסקיות",
    ],
    excerpt:
      "כל יום מתפרסמים בקבוצות פייסבוק פוסטים שמחפשים בעל מקצוע, והשאלה הראשונה היא אם להגיב בפוסט או לשלוח הודעה פרטית. התשובה היא גם וגם: המדריך מסביר מה עושה כל ערוץ, מתי עונים בכלל, ומביא דוגמאות לתגובה ולהודעה הפרטית, לפני ואחרי.",
  },
  // 34 - leads-for-dental-clinics
  {
    slug: "leads-for-dental-clinics",
    image: "/blog-images/leads-for-dental-clinics.png",
    title:
      "לידים למרפאות שיניים: איך מגיעים למטופלים שמחפשים רופא שיניים בקבוצות פייסבוק",
    metaTitle: "לידים למרפאות שיניים ולרופאי שיניים - מקבוצות פייסבוק",
    metaDescription:
      "לידים למרפאות שיניים מקבוצות פייסבוק: איפה מטופלים מבקשים המלצה על רופא שיניים, מה מותר לכתוב בתגובה לפי תקנות הפרסומת ואיך עונים כדי שיפנו אליכם.",
    datePublished: "2026-09-22",
    dateModified: "2026-09-24",
    tags: [
      "מרפאות שיניים",
      "רופאי שיניים",
      "לידים",
      "קבוצות פייסבוק",
      "לקוחות פוטנציאליים",
    ],
    excerpt:
      "מטופלים מחפשים רופא שיניים בקבוצת הפייסבוק של העיר, בפוסט קצר על כאב, שן בינה, שתל או יישור שיניים. המדריך מסביר איפה נמצאות הפניות האלה, מה תקנות הפרסומת של רופאי השיניים מתירות לכתוב בתגובה, ואיך תשובה אחת בשרשור הופכת למטופל חדש ולהמלצות.",
  },
  // 35 - freelancer-no-clients
  {
    slug: "freelancer-no-clients",
    image: "/blog-images/freelancer-no-clients.png",
    title:
      "יצאתי לעצמאות ואין עבודה: איך ממלאים את היומן בחודשים הראשונים",
    metaTitle: "יצאתם לעצמאות ואין עבודה? כך משיגים את הלקוחות הראשונים",
    metaDescription:
      "יצאתם לעצמאות והיומן ריק? תוכנית 90 יום לפרילנסרים ולנותני שירות: עבודה בחינם מול פיילוט בתשלום, המעגל הקרוב, מפה לאוזן ושרשורי המלצות.",
    datePublished: "2026-10-03",
    dateModified: "2026-10-03",
    tags: [
      "עצמאים",
      "פרילנסרים",
      "לקוחות ראשונים",
      "מפה לאוזן",
      "לקוחות פוטנציאליים",
    ],
    excerpt:
      "יצאתם לעצמאות לפני כמה חודשים והיומן עדיין ריק? המדריך מסביר למה זה קורה גם לאנשי מקצוע מצוינים, מתי (כמעט אף פעם) לעבוד בחינם בשביל תיק עבודות, איך מבקשים עבודה מהמעגל הקרוב, ואיך בונים תוכנית של 90 יום שממלאת את היומן.",
  },
  // 36 - lead-management-small-business
  {
    slug: "lead-management-small-business",
    image: "/blog-images/lead-management-small-business.png",
    title: "ניהול לידים לעסק קטן: איך לא לאבד אף לקוח פוטנציאלי - עם מערכת לניהול לידים או בלעדיה",
    metaTitle: "מערכת לניהול לידים לעסק קטן: שיטה שלא מאבדת אף לקוח",
    metaDescription: "ניהול לידים לעסק קטן: איזו מערכת מתאימה, כמה מהר לענות לפנייה, איך מדרגים לקוחות פוטנציאליים, חמש תוויות וסקירה שבועית של עשר דקות.",
    datePublished: "2026-10-03",
    dateModified: "2026-11-03",
    tags: [
      "ניהול לידים",
      "מערכת לניהול לידים",
      "לקוחות פוטנציאליים",
      "הזדמנויות עסקיות",
      "מעקב",
      "עסק קטן",
    ],
    excerpt: "עסק קטן לא מאבד לקוחות בגלל מחסור בפניות, אלא כי הן מפוזרות בין הווטסאפ, הפתקים והזיכרון. המדריך משווה גיליון, מערכת לניהול לקוחות וכלי ייעודי, מסביר כמה מהר לענות ומה לכתוב קודם, איך מדרגים פניות ומה סוכני AI יכולים לעשות בזה, ומציג שיטה של חמש תוויות וסקירה שבועית.",
  },
  // 37 - facebook-group-auto-posting
  {
    slug: "facebook-group-auto-posting",
    image: "/blog-images/facebook-group-auto-posting.png",
    title:
      "פרסום אוטומטי בקבוצות פייסבוק: למה זה נחסם - ומה עובד במקום",
    metaTitle: "פרסום אוטומטי בקבוצות פייסבוק: למה זה נחסם ומה עובד במקום",
    metaDescription:
      "פרסום אוטומטי בקבוצות פייסבוק נראה כמו קיצור דרך, אבל רוב המודעות נדחות או נבלעות. מה קורה לפוסט, מה אומרת מדיניות הספאם של מטא, ומה עובד במקום.",
    datePublished: "2026-10-03",
    dateModified: "2026-10-03",
    tags: [
      "פרסום בקבוצות פייסבוק",
      "תוכנה לפרסום בקבוצות",
      "ספאם",
      "לקוחות פוטנציאליים",
      "הזדמנויות עסקיות",
    ],
    excerpt:
      null,
  },
  // 38 - buying-leads-israel-guide
  {
    slug: "buying-leads-israel-guide",
    image: "/blog-images/buying-leads-israel-guide.png",
    title:
      "קניית לידים: כמה עולה ליד בישראל ומה בודקים לפני שמשלמים",
    metaTitle: "קניית לידים: כמה עולה ליד בישראל ומה בודקים לפני שמשלמים",
    metaDescription:
      "קניית לידים לעסק: כמה עולה ליד בישראל, איך מחשבים עלות ללקוח משלם ושש שאלות לחוזה - מול חיפוש לקוחות פוטנציאליים והזדמנויות עסקיות.",
    datePublished: "2026-10-19",
    dateModified: "2026-10-19",
    tags: [
      "קניית לידים",
      "מחיר ליד",
      "לקוחות פוטנציאליים",
      "הזדמנויות עסקיות",
      "ספקי לידים",
    ],
    excerpt:
      "שוקלים לקנות לידים? המדריך מסביר כמה עולה ליד בישראל לפי מחירונים שפורסמו, למה מחיר הליד אינו המספר שקובע, איך מחשבים עלות ללקוח משלם, אילו שש שאלות לשאול לפני שחותמים - ומתי קניית לידים דווקא משתלמת.",
  },
  // 39 - health-insurance-pension-leads
  {
    slug: "health-insurance-pension-leads",
    image: "/blog-images/health-insurance-pension-leads.png",
    title:
      "לידים לביטוח בריאות ופנסיה: מאיפה מגיעות פניות אמיתיות - ומה מותר לסוכן",
    metaTitle: "לידים לביטוח בריאות ופנסיה: מאיפה מגיעות פניות אמיתיות",
    metaDescription:
      "לידים לביטוח בריאות ופנסיה: מה גורם ללקוחות פוטנציאליים לשאול, כמה עולה ליד קנוי, מה בודקים לפני שקונים ואיך מזהים הזדמנויות עסקיות אמיתיות.",
    datePublished: "2026-10-19",
    dateModified: "2026-10-19",
    tags: [
      "סוכני ביטוח",
      "ביטוח בריאות",
      "פנסיה",
      "קניית לידים",
      "לקוחות פוטנציאליים",
    ],
    excerpt:
      "רפורמת ביטוחי הבריאות, כפל ביטוחים ובדיקה עצמית בהר הביטוח מביאים אנשים לשאול בפומבי על ביטוח בריאות ופנסיה. המדריך מסביר מאיפה מגיעות הפניות האלה, כמה עולה ליד קנוי, מה הרגולציה אומרת על קניית לידים, ואיך מזהים פנייה חמה ועונים לה בזמן.",
  },
  // 40 - facebook-lead-campaign-stopped
  {
    slug: "facebook-lead-campaign-stopped",
    image: "/blog-images/facebook-lead-campaign-stopped.png",
    title:
      "קמפיין לידים בפייסבוק הפסיק להביא לידים? למה זה קורה ומה בודקים קודם",
    metaTitle: "קמפיין לידים בפייסבוק הפסיק להביא לידים? מה בודקים קודם",
    metaDescription:
      "קמפיין לידים בפייסבוק נדם? סדר בדיקה מהזול ליקר: טופס וחיבורים, סטטוס המודעה, שלב הלמידה, עייפות הקהל והעונה - ומה לא לעשות כשהלידים יורדים.",
    datePublished: "2026-10-26",
    dateModified: "2026-10-26",
    tags: [
      "קמפיין לידים בפייסבוק",
      "פרסום ממומן",
      "שלב הלמידה",
      "לקוחות פוטנציאליים",
      "הזדמנויות עסקיות",
    ],
    excerpt:
      "קמפיין הלידים בפייסבוק עבד יפה ופתאום נדם? המדריך מסדר את הבדיקות מהזול ליקר: האם הלידים בכלל מגיעים אליכם, האם המודעה רצה, האם עריכה החזירה את הקמפיין לשלב הלמידה, האם הקהל התעייף ומתי זו באמת העונה. ובנוסף: מה לא לעשות בימים הראשונים של הירידה.",
  },
  // 41 - small-business-sales-help
  {
    slug: "small-business-sales-help",
    image: "/blog-images/small-business-sales-help.png",
    title:
      "לא אוהבים למכור? איש מכירות, שותף או חברת מכירות - מה באמת מתאים לעסק קטן",
    metaTitle: "איש מכירות לעסק קטן: עובד שכיר, שותף או חברת מכירות?",
    metaDescription:
      "איש מכירות לעסק קטן: כמה עולה שכיר, מתי מתאים סוכן על עמלות בלבד, שותף או חברת מכירות - ואיך מוכרים פחות כשעונים ללקוחות פוטנציאליים שכבר מבקשים.",
    datePublished: "2026-10-26",
    dateModified: "2026-10-26",
    tags: [
      "איש מכירות לעסק קטן",
      "גיוס אנשי מכירות",
      "שותף לעסק",
      "חברת מכירות",
      "לקוחות פוטנציאליים",
    ],
    excerpt:
      "טובים במקצוע אבל לא אוהבים למכור? המדריך משווה בין איש מכירות שכיר, סוכן על עמלות, שותף שאחראי על המכירות וחברת מכירות חיצונית. תמצאו כאן כמה זה עולה, מתי כל אחד מתאים, מתי מוקדם מדי לגייס, ואיך מצמצמים את הצורך למכור מלכתחילה.",
  },
  // 42 - when-customers-search
  {
    slug: "when-customers-search",
    image: "/blog-images/when-customers-search.png",
    title:
      "מתי הלקוחות שלכם מחפשים? שעות שיא, מהירות תגובה ומה המספרים של העסק מגלים",
    metaTitle: "מתי לקוחות מחפשים שירות? שעות שיא ומהירות תגובה בעסק",
    metaDescription:
      "מתי לקוחות פוטנציאליים מחפשים את השירות שלכם? חמש שאלות על שעות שיא, מהירות תגובה ומקורות פניות, ומה עושים עם כל תשובה בעסק שלכם.",
    datePublished: "2026-10-26",
    dateModified: "2026-10-26",
    tags: [
      "שעות שיא",
      "מהירות תגובה",
      "לקוחות פוטנציאליים",
      "הזדמנויות עסקיות",
      "ניתוח נתונים לעסק קטן",
    ],
    excerpt:
      "רוב בעלי העסקים מנחשים מתי מגיעות פניות וכמה מהר הם עונים. המדריך מציג חמש שאלות שהמספרים של העסק עונים עליהן - שעות שיא, מהירות תגובה, מקורות, הזדמנויות שמחכות ומגמה - ומה עושים עם כל תשובה.",
  },
  // 43 - accountant-new-clients
  {
    slug: "accountant-new-clients",
    image: "/blog-images/accountant-new-clients.png",
    title: "לקוחות חדשים לרואה חשבון: איך שאלת מס בקבוצת פייסבוק הופכת ללקוח - ומתי הכי כדאי להיות שם",
    metaTitle: "לקוחות חדשים לרואה חשבון - שאלות מס בקבוצות פייסבוק",
    metaDescription: "איך רואה חשבון משיג לקוחות חדשים מקבוצות פייסבוק: אילו שאלות מס מסמנות לקוח פוטנציאלי, איך עונים בפומבי ולמה דצמבר וינואר הם הזמן להיות שם.",
    datePublished: "2026-11-03",
    dateModified: "2026-11-03",
    tags: [
      "רואי חשבון",
      "לקוחות פוטנציאליים",
      "קבוצות פייסבוק",
      "שיווק למשרד רואי חשבון",
      "עוסק פטור",
    ],
    excerpt: "בקבוצות עסקים מעטים כותבים \"מחפש רואה חשבון\", ורבים שואלים שאלת מס בפומבי: פטור או מורשה, תלוש ראשון, תיאום מס. המדריך מסביר למה השאלה היא ההזדמנות, איך עונים עליה כך שיפנו אליכם, מה עונים למי שרוצה להחליף רואה חשבון ולמה דצמבר וינואר הם החודשים להיות שם.",
  },
  // 44 - bookkeeper-retainer-clients
  {
    slug: "bookkeeper-retainer-clients",
    image: "/blog-images/bookkeeper-retainer-clients.png",
    title: "לקוחות למנהלת חשבונות: איך בונים תיק לקוחות חודשי (ריטיינר) בלי לרדוף אחרי כל לקוח",
    metaTitle: "לקוחות למנהלת חשבונות - איך בונים תיק לקוחות חודשי",
    metaDescription: "לקוחות למנהלת חשבונות ולחשבת שכר: אילו פוסטים מבשרים על לקוח חודשי, איך עונים על שאלת שכר בפומבי ואיך הופכים עבודה חד פעמית לריטיינר.",
    datePublished: "2026-11-03",
    dateModified: "2026-11-03",
    tags: [
      "מנהלי חשבונות",
      "חשבי שכר",
      "לקוחות פוטנציאליים",
      "ריטיינר",
      "קבוצות פייסבוק",
    ],
    excerpt: "למנהלת חשבונות או לחשבת שכר, לקוח אחד שמשלם כל חודש שווה לרוב יותר מהרבה עבודות חד פעמיות. המדריך מראה אילו פוסטים בקבוצות מבשרים על צורך חוזר, איך עונים על שאלת שכר בפומבי, ואיך מציעים חבילה חודשית שמתחדשת מעצמה.",
  },
  // 45 - b2b-customers-small-business
  {
    slug: "b2b-customers-small-business",
    image: "/blog-images/b2b-customers-small-business.png",
    title: "לקוחות עסקיים לעסק קטן: איך מגיעים למקבלי ההחלטות בחברות ובמוסדות",
    metaTitle: "לקוחות עסקיים לעסק קטן: איך מגיעים למקבלי החלטות בחברות",
    metaDescription: "לקוחות עסקיים לעסק קטן: מי מחליט בכל ארגון, איך מגיעים אליו בדרך חמה, ומה מותר לכתוב בפנייה ראשונה לעסק לפי חוק התקשורת.",
    datePublished: "2026-11-03",
    dateModified: "2026-11-03",
    tags: [
      "לקוחות עסקיים",
      "לידים B2B",
      "מקבלי החלטות",
      "שיתופי פעולה עם חברות",
      "לקוחות פוטנציאליים",
      "הזדמנויות עסקיות",
    ],
    excerpt: "מוכרים לחברות ולמוסדות ולא יודעים למי לפנות? המדריך מסביר מי מחליט בעסק קטן ומי בארגון גדול, איך מגיעים אליו דרך היכרות, לקוח קיים או ספק משלים, ומה מותר לכתוב בהודעה הראשונה לעסק לפי חוק התקשורת. ובנוסף: למה פוסט של עסק שמחפש ספק הוא ההזדמנות הטובה ביותר.",
  },
];

// ---------------------------------------------------------------------------
// Sorting (/blog index)
// ---------------------------------------------------------------------------
//
// The `articleIndex` array above is authored in rough chronological order, i.e.
// OLDEST first — which is not what a blog index should lead with. Ordering is
// therefore always applied explicitly via sortArticles(); nothing should render
// the raw array order.
//
// DEFAULT_SORT is load-bearing for SEO: /blog is prerendered at build time with
// no query param (scripts/prerender.js), so whatever DEFAULT_SORT produces is
// the order baked into the static HTML that crawlers read.

export const SORT_NEWEST = "newest";
export const SORT_OLDEST = "oldest";
export const SORT_ALPHA = "alpha";

export const DEFAULT_SORT = SORT_NEWEST;

export const SORT_OPTIONS = [
  { value: SORT_NEWEST, label: "החדשים ביותר" },
  { value: SORT_OLDEST, label: "הוותיקים ביותר" },
  { value: SORT_ALPHA, label: "לפי א-ב" },
];

const SORT_VALUES = new Set(SORT_OPTIONS.map((o) => o.value));

/**
 * Coerce an untrusted sort key (e.g. from `?sort=`) to a supported one.
 * Unknown/absent values fall back to DEFAULT_SORT, so a hand-edited URL can
 * never render an undefined order.
 *
 * @param {string|null} value Raw query-param value.
 * @returns {string} A member of SORT_OPTIONS.
 */
export function normalizeSort(value) {
  return SORT_VALUES.has(value) ? value : DEFAULT_SORT;
}

// `datePublished` is a zero-padded "YYYY-MM-DD" (enforced by the structural
// contract in src/__tests__/seoContentSync.test.js), so plain string comparison
// is a correct chronological compare — no Date parsing, no timezone drift.
// Compared as ASCII (undefined locale) because these are digits and hyphens, not
// prose: collation must not enter into it.
const byDateDesc = (a, b) =>
  b.datePublished < a.datePublished ? -1 : b.datePublished > a.datePublished ? 1 : 0;

/**
 * Collator for article titles, PINNED to Hebrew.
 *
 * Exported so the pin itself is assertable (`resolvedOptions().locale === "he"`).
 * That matters more than it looks: this project's dev machine has an ICU default
 * locale of he-IL, so an unpinned `localeCompare(x)` produces identical output
 * HERE and silently different output on a CI/dev box defaulting to en-US. The
 * difference is not theoretical — Hebrew collation sorts Latin-initial titles
 * AFTER Hebrew ones, en collation sorts them BEFORE, and the blog has exactly
 * such a title ("Vibe Coding: ..."). It would move from last to first.
 *
 * An explicit Intl.Collator (rather than a per-comparison localeCompare) is also
 * the documented way to sort: the collator is built once instead of per compare.
 */
export const titleCollator = new Intl.Collator("he");

const byTitleHe = (a, b) => titleCollator.compare(a.title, b.title);

const COMPARATORS = {
  [SORT_NEWEST]: byDateDesc,
  [SORT_OLDEST]: (a, b) => byDateDesc(b, a),
  [SORT_ALPHA]: byTitleHe,
};

/**
 * Sort ANY list of articles (index entries or fully-composed articles).
 *
 * Generic so the light homepage strip and the /blog index share one
 * comparator set: a second implementation would let the two surfaces
 * disagree about which article is newest.
 *
 * Never sorts in place - the caller's array may be module-level shared state.
 *
 * TIE-BREAK: dates are whole days and 10 of the 11 articles share a date with
 * at least one other (6 alone on 2026-06-30). Neither date comparator breaks
 * those ties, so their relative order comes from Array.prototype.sort's
 * STABILITY, which ES2019 guarantees: tied articles keep authored order. That
 * is a deliberate, specified reliance - it is what makes the default /blog
 * order deterministic and reproducible in the prerendered HTML.
 *
 * @param {Array<object>} list Articles or index entries.
 * @param {string} [sort] A SORT_OPTIONS value; anything else uses DEFAULT_SORT.
 * @returns {Array<object>} Sorted copy.
 */
export function sortArticleList(list, sort = DEFAULT_SORT) {
  return [...list].sort(COMPARATORS[normalizeSort(sort)]);
}

/**
 * Sorted LIGHT index - for eagerly-bundled callers (the homepage strip) that
 * must not pull in blogBodies. Same comparators as sortArticles, and the index
 * has the same order and dates as the composed articles, so both surfaces
 * agree on what is newest.
 *
 * @param {string} [sort] A SORT_OPTIONS value.
 * @returns {Array<object>} Sorted copy of articleIndex.
 */
export function sortArticleIndex(sort = DEFAULT_SORT) {
  return sortArticleList(articleIndex, sort);
}
